/**
 * WatchRuleSettingsPanel — standardized settings card + shared edit dialog.
 *
 * Read-only card:  compact hover-highlight rows, consistent across every policy.
 * Edit modal:      Shared RuleDialog in conditionOnly mode (formula + type pre-locked).
 * Empty state:     "Configure" button that auto-creates the rule with defaults.
 */

import React from 'react';
import { CircularProgress } from '@mui/material';
import { Edit as EditIcon, Add as AddIcon } from '@mui/icons-material';
import type { WatchRule, RuleTypeMeta, ConditionField } from '../../../hooks/useWatchRules';
import { RuleDialog } from '../../../components/WatchRules';

// ─── Public props ───────────────────────────────────────────────────

interface Props {
  rule: WatchRule | undefined;
  ruleMeta: RuleTypeMeta | undefined;
  onSave: (_ruleId: number, _condition: Record<string, any>) => Promise<void>;
  isSaving?: boolean;
  /** Called when no rule exists and user clicks "Configure". Parent should create the rule. */
  onCreateRule?: () => void;
  isCreating?: boolean;
  /** The formula currently being viewed — used to detect "inherited" account-wide rules */
  viewingFormulaId?: number | null;
  /** Display name of the formula being viewed (e.g. "Cocoa Butter") */
  viewingFormulaName?: string;
}

// ─── Main component ─────────────────────────────────────────────────

export const WatchRuleSettingsPanel: React.FC<Props> = ({ rule, ruleMeta, onSave, isSaving, onCreateRule, isCreating, viewingFormulaId, viewingFormulaName }) => {
  const [modalOpen, setModalOpen] = React.useState(false);

  // No rule yet — show a "Configure" prompt
  if (!rule || !ruleMeta) {
    return (
      <div className="px-4 py-4 border-b border-gray-200">
        <div className="flex items-center justify-between">
          <span className="text-sm text-gray-500">No alert rule configured yet.</span>
          {onCreateRule && (
            <button
              onClick={onCreateRule}
              disabled={isCreating}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors disabled:opacity-50"
            >
              {isCreating ? <CircularProgress size={12} color="inherit" /> : <AddIcon sx={{ fontSize: 14 }} />}
              Configure
            </button>
          )}
        </div>
      </div>
    );
  }

  const schema = ruleMeta.conditionSchema;
  const condition = rule.condition || {};
  const primaryFields = schema.filter(f => !f.advanced);
  const advancedFields = schema.filter(f => f.advanced);

  const handleDialogSave = (data: any) => {
    onSave(rule.id, data.condition);
    setModalOpen(false);
  };

  return (
    <>
      {/* ── Read-only card ── */}
      <div className="px-4 py-3 border-b border-gray-200">
        {/* Header */}
        <div className="flex items-center justify-between mb-2">
          <div className="flex items-baseline gap-2 min-w-0">
            <h4 className="text-sm font-semibold text-gray-800 truncate">{ruleMeta.label}</h4>
            {rule.formula_id === null && viewingFormulaId && (
              <span className="px-1.5 py-0.5 text-[9px] font-medium rounded bg-amber-50 text-amber-700 border border-amber-200 whitespace-nowrap relative -top-px">
                Account-wide
              </span>
            )}
          </div>
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {rule.formula_id === null && viewingFormulaId && onCreateRule && (
              <button
                onClick={onCreateRule}
                disabled={isCreating}
                className="inline-flex items-center gap-0.5 px-2 py-1 text-[10px] font-medium text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors disabled:opacity-50"
                title="Create a formula-specific override"
              >
                {isCreating ? <CircularProgress size={10} color="inherit" /> : <AddIcon sx={{ fontSize: 12 }} />}
                Override
              </button>
            )}
            <button
              onClick={() => setModalOpen(true)}
              className="inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors"
            >
              <EditIcon sx={{ fontSize: 14 }} />
              Edit
            </button>
          </div>
        </div>

        {/* Inherited disclaimer */}
        {rule.formula_id === null && viewingFormulaId && (
          <div className="mb-2.5 px-2.5 py-2 rounded-md bg-amber-50/60 border border-amber-100 text-[11px] text-amber-800 leading-relaxed">
            This is an account-wide rule that applies to all materials.
            {viewingFormulaName && <> <strong>{viewingFormulaName}</strong> inherits these settings.</>}
            {' '}To set a different threshold for this material, create a specific override.
          </div>
        )}

        {/* Primary rows */}
        <div className="-mx-1 space-y-0.5">
          {primaryFields.map(field => (
            <ReadOnlyRow key={field.key} field={field} value={condition[field.key]} />
          ))}
        </div>

        {/* Advanced rows — muted */}
        {advancedFields.length > 0 && (
          <div className="mt-2 pt-2 border-t border-gray-100 -mx-1 space-y-0.5">
            {advancedFields.map(field => (
              <ReadOnlyRow key={field.key} field={field} value={condition[field.key]} muted />
            ))}
          </div>
        )}
      </div>

      {/* ── Shared edit dialog (condition-only mode) ── */}
      <RuleDialog
        open={modalOpen}
        rule={rule}
        onClose={() => setModalOpen(false)}
        onSave={handleDialogSave}
        isSaving={!!isSaving}
        formulas={[]}
        ruleTypes={ruleMeta ? [ruleMeta] : []}
        lockedRuleType={rule.rule_type}
        lockedFormulaId={rule.formula_id || undefined}
        conditionOnly
      />
    </>
  );
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

// ─── Read-only row (hover highlight) ────────────────────────────────

const ReadOnlyRow: React.FC<{ field: ConditionField; value: any; muted?: boolean }> = ({ field, value, muted }) => {
  // Monthly array → mini bar chart
  if (field.type === 'number_array') {
    const arr = Array.isArray(value) && value.length > 0 ? value : null;
    if (!arr) return <ValueRow label={field.label} display="not set" muted italic />;
    const currMonth = new Date().getMonth();
    const maxVal = Math.max(...arr.slice(0, 12), 1);
    return (
      <div className="mt-3 mb-2 px-1">
        <div className="text-xs text-gray-500 mb-2">{field.label}</div>
        <div className="flex gap-0.5">
          {arr.slice(0, 12).map((v: number, i: number) => {
            const ratio = v / maxVal;
            const pct = Math.round(ratio * 100);
            // HSL blue gradient: high → saturated dark blue, low → light blue
            const sat = Math.round(40 + ratio * 50);   // 40-90%
            const light = Math.round(80 - ratio * 40);  // 80-40%
            return (
              <div key={i} className="flex-1 flex flex-col items-center gap-0.5">
                <div className="text-[9px] text-gray-400 leading-none">{MONTH_NAMES[(currMonth + i) % 12]}</div>
                <div
                  className="w-full rounded-sm flex items-end"
                  style={{ height: 20, backgroundColor: '#f3f4f6' }}
                >
                  <div
                    className="w-full rounded-sm"
                    style={{
                      height: pct > 0 ? Math.max(pct * 0.2, 2) : 0,
                      backgroundColor: v > 0 ? `hsl(217, ${sat}%, ${light}%)` : 'transparent',
                    }}
                  />
                </div>
                <div className="text-[9px] font-medium text-gray-600 tabular-nums leading-none">{v}%</div>
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  // Select
  if (field.type === 'select' && field.options) {
    const opt = field.options.find(o => o.value === String(value));
    return <ValueRow label={field.label} display={opt?.label || String(value ?? '—')} muted={muted} />;
  }

  // Scalar
  return <ValueRow label={field.label} display={formatValue(field, value)} muted={muted} />;
};

/** Simple row with hover highlight for readability */
const ValueRow: React.FC<{ label: string; display: string; muted?: boolean; italic?: boolean }> = ({ label, display, muted, italic }) => (
  <div className={`flex items-center justify-between px-1 py-[3px] rounded hover:bg-gray-50 transition-colors text-xs ${muted ? 'opacity-50' : ''}`}>
    <span className="text-gray-500">{label}</span>
    <span className={`font-medium tabular-nums ${italic ? 'italic text-gray-400' : 'text-gray-700'}`}>{display}</span>
  </div>
);

function formatValue(field: ConditionField, value: any): string {
  const v = value ?? field.default ?? 0;
  if (field.type === 'currency') return `$${Math.abs(Number(v)).toLocaleString()}`;
  if (field.type === 'percent') return `${v}%`;
  return `${v}${field.unit ? ` ${field.unit}` : ''}`;
}

